import { useMemo } from 'react';
import { SuitcaseIconStatic } from './SuitcaseIconStatic';

interface SuitcaseIconAnimatedProps {
  width?: number;
  height?: number;
  className?: string;
  duration?: number;
  easing?: string;
  color?: string;
  hoverColor?: string;
  strokeWidth?: number;
}

export const SuitcaseIconAnimated = ({
  width = 20,
  height = 20,
  className,
  duration = 0.5,
  easing = 'ease-in-out',
  color = 'currentColor',
  hoverColor = color,
  strokeWidth = 1.64
}: SuitcaseIconAnimatedProps) => {
  // Pre-calculate path data for better performance
  const pathData = useMemo(() => {
    // Normal state values
    const normalTopCurve = "M0.820312 5.82007C0.820312 4.7155 1.71574 3.82007 2.82031 3.82007H17.1803C18.2849 3.82007 19.1803 4.7155 19.1803 5.82007V17.1801C19.1803 18.2846 18.2849 19.1801 17.1803 19.1801H2.82031C1.71574 19.1801 0.820312 18.2846 0.820312 17.1801V5.82007Z";
    const normalMiddleCurve = "M6.41031 12.1801H2.82031C1.71574 12.1801 0.820312 11.2846 0.820312 10.1801V5.82007C0.820312 4.7155 1.71574 3.82007 2.82031 3.82007H17.1803C18.2849 3.82007 19.1803 4.7155 19.1803 5.82007V10.1801C19.1803 11.2846 18.2849 12.1801 17.1803 12.1801H13.5903";
    const normalHandle = "M13.1803 3.18007V1.82007C13.1803 1.26778 12.7326 0.820068 12.1803 0.820068H7.82031C7.26803 0.820068 6.82031 1.26778 6.82031 1.82007V3.18007";
    const normalBottomSquare = "M6.82387 11.5297C6.82472 10.978 7.27219 10.5312 7.82387 10.5312H12.1839C12.7368 10.5312 13.1847 10.9799 13.1839 11.5328L13.1819 12.8296C13.181 13.3813 12.7335 13.8281 12.1819 13.8281H7.82186C7.26897 13.8281 6.82101 13.3794 6.82186 12.8265L6.82387 11.5297Z";

    // Hovered state values - with more movement
    const hoveredTopCurve = "M0.820312 2.82007C0.820312 1.7155 1.71574 0.820068 2.82031 0.820068H17.1803C18.2849 0.820068 19.1803 1.7155 19.1803 2.82007V17.1801C19.1803 18.2846 18.2849 19.1801 17.1803 19.1801H2.82031C1.71574 19.1801 0.820312 18.2846 0.820312 17.1801V2.82007Z";
    const hoveredMiddleCurve = "M6.41031 9.1801H2.82031C1.71574 9.1801 0.820312 8.2846 0.820312 7.1801V2.82007C0.820312 1.7155 1.71574 0.820068 2.82031 0.820068H17.1803C18.2849 0.820068 19.1803 1.7155 19.1803 2.82007V7.1801C19.1803 8.2846 18.2849 9.1801 17.1803 9.1801H13.5903";
    const hoveredHandle = "M13.1803 0.180068V-1.17993C13.1803 -1.73222 12.7326 -2.17993 12.1803 -2.17993H7.82031C7.26803 -2.17993 6.82031 -1.73222 6.82031 -1.17993V0.180068";
    const hoveredBottomSquare = "M6.82387 8.5297C6.82472 7.978 7.27219 7.5312 7.82387 7.5312H12.1839C12.7368 7.5312 13.1847 7.9799 13.1839 8.5328L13.1819 9.8296C13.181 10.3813 12.7335 10.8281 12.1819 10.8281H7.82186C7.26897 10.8281 6.82101 10.3794 6.82186 9.8265L6.82387 8.5297Z";

    return {
      topCurve: normalTopCurve,
      hoveredTopCurve,
      middleCurve: normalMiddleCurve,
      hoveredMiddleCurve,
      handle: normalHandle,
      hoveredHandle,
      bottomSquare: normalBottomSquare,
      hoveredBottomSquare
    };
  }, []);

  // Convert CSS ease-in-out to SVG ease equivalent
  const svgEasing = easing === 'ease-in-out' ? 'ease' : easing;

  // SVG animation properties for continuous animation
  const animationAttributes = {
    dur: `${duration * 2}s`,
    begin: 'suitcaseParentGroup.mouseover',
    end: 'suitcaseParentGroup.mouseout',
    repeatCount: 'indefinite',
    calcMode: 'spline',
    keySplines: '0.42 0 0.58 1; 0.42 0 0.58 1',
    keyTimes: '0; 0.5; 1',
    restart: 'whenNotActive'
  };

  return (
    <div className='group'>
      {/* Static version - visible by default, hidden on hover */}
      <div className={`group-hover:hidden ${className}`} style={{ width, height }}>
        <SuitcaseIconStatic
          width={width}
          height={height}
          color={color}
          strokeWidth={strokeWidth}
        />
      </div>

      {/* Animated version - hidden by default, visible on hover */}
      <div className={`hidden group-hover:block ${className}`} style={{ width, height }}>
        <svg
          width={width}
          height={height}
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g id="suitcaseParentGroup">
            <rect width="20" height="20" fill="none">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0 0; 0 0.2; 0 0"
                {...animationAttributes}
              />
              <animateTransform
                attributeName="transform"
                type="rotate"
                additive="sum"
                values="0 10 10; 0.4 10 10; 0 10 10"
                {...animationAttributes}
              />
            </rect>

            <path
              d={pathData.topCurve}
              stroke={color}
              strokeWidth={strokeWidth}
              fill="none"
            >
              <animate
                attributeName="d"
                values={`${pathData.topCurve}; ${pathData.hoveredTopCurve}; ${pathData.topCurve}`}
                {...animationAttributes}
              />
              <animate
                attributeName="stroke"
                values={`${color}; ${hoverColor}; ${color}`}
                {...animationAttributes}
              />
            </path>

            <path
              d={pathData.middleCurve}
              stroke={color}
              strokeWidth={strokeWidth}
              fill="none"
            >
              <animate
                attributeName="d"
                values={`${pathData.middleCurve}; ${pathData.hoveredMiddleCurve}; ${pathData.middleCurve}`}
                {...animationAttributes}
              />
              <animate
                attributeName="stroke"
                values={`${color}; ${hoverColor}; ${color}`}
                {...animationAttributes}
              />
            </path>

            <path
              d={pathData.handle}
              stroke={color}
              strokeWidth={strokeWidth}
              fill="none"
            >
              <animate
                attributeName="d"
                values={`${pathData.handle}; ${pathData.hoveredHandle}; ${pathData.handle}`}
                {...animationAttributes}
              />
              <animate
                attributeName="stroke"
                values={`${color}; ${hoverColor}; ${color}`}
                {...animationAttributes}
              />
            </path>

            <path
              d={pathData.bottomSquare}
              stroke={color}
              strokeWidth={strokeWidth}
              fill="none"
            >
              <animate
                attributeName="d"
                values={`${pathData.bottomSquare}; ${pathData.hoveredBottomSquare}; ${pathData.bottomSquare}`}
                {...animationAttributes}
              />
              <animate
                attributeName="stroke"
                values={`${color}; ${hoverColor}; ${color}`}
                {...animationAttributes}
              />
            </path>

            <rect
              width="20"
              height="20"
              fill="transparent"
              style={{ cursor: 'pointer' }}
            />
          </g>
        </svg>
      </div>
    </div>
  );
};
